import { BillChargeKind, computeBillShares } from "@boo/shared";
import type { BillShares } from "@boo/shared";
import type { EditorCharge, EditorItem, EditorPerson } from "../types/bills.types";

const round2 = (n: number) => Math.round(n * 100) / 100;

export function formatBaht(n: number): string {
  return `฿${n.toLocaleString("en-US", { maximumFractionDigits: 2 })}`;
}

export function billTotal(items: EditorItem[], charges: EditorCharge[]) {
  return round2(
    items.reduce((s, i) => s + i.amount, 0) +
      charges.reduce((s, c) => s + c.amount, 0),
  );
}

/**
 * Recompute charges that carry a `percent`: service is a % of the items, VAT a
 * % of items + service. A charge without `percent` (typed by hand, saved or
 * scanned) is left alone.
 */
export function applyPercentCharges(
  items: EditorItem[],
  charges: EditorCharge[],
): EditorCharge[] {
  const itemsTotal = items.reduce((s, i) => s + i.amount, 0);
  const withPercent = (base: number, c: EditorCharge) =>
    c.percent == null ? c : { ...c, amount: round2((base * c.percent) / 100) };

  const afterService = charges.map((c) =>
    c.kind === BillChargeKind.Service ? withPercent(itemsTotal, c) : c,
  );
  const serviceTotal = afterService
    .filter((c) => c.kind === BillChargeKind.Service)
    .reduce((s, c) => s + c.amount, 0);
  return afterService.map((c) =>
    c.kind === BillChargeKind.Vat ? withPercent(itemsTotal + serviceTotal, c) : c,
  );
}

export function sharesFor(
  people: EditorPerson[],
  items: EditorItem[],
  charges: EditorCharge[],
  goDutch = false,
): BillShares {
  return computeBillShares(
    people.map((p) => p.id),
    items.map((i) => ({ id: i.key, amount: i.amount, claimedBy: i.claimedBy })),
    charges,
    goDutch ? { ownerId: people.find((p) => p.isOwner)?.id } : undefined,
  );
}

/** "Beer x3 ฿360" → three ฿120 lines that can be assigned separately. */
export function explodeUnits(item: EditorItem): EditorItem[] {
  if (item.quantity <= 1) return [item];
  const each = round2(item.amount / item.quantity);
  return Array.from({ length: item.quantity }, (_, i) => ({
    key: crypto.randomUUID(),
    name: `${item.name} (${i + 1}/${item.quantity})`,
    // last unit takes the rounding remainder so the total is unchanged
    amount:
      i === item.quantity - 1
        ? round2(item.amount - each * (item.quantity - 1))
        : each,
    quantity: 1,
    claimedBy: item.claimedBy,
  }));
}
